import React from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    Grid,
    GridItem,
    Box,
    Text,
    Heading,
    Image,
    VStack,
} from '@chakra-ui/react';
import type { TphDetailsWithCoords } from '../types';
import { API_BASE_URL } from '../../../auth/apiClient';
import { getDistance } from '../utils/utils';

interface MapMarkerDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    type: 'tph' | 'harvest' | null;
    tph: TphDetailsWithCoords | null;
    foremanGroupName?: string | null;
}

const DetailItem = ({ label, value }: { label: string; value?: string | number | React.ReactNode }) => (
    <Box>
        <Text fontSize="sm" color="gray.500" mb={1}>
            {label}
        </Text>
        {typeof value === 'string' || typeof value === 'number' ? (
            <Text fontWeight="500">{value !== undefined && value !== null ? value : "-"}</Text>
        ) : (
            value || <Text fontWeight="500">-</Text>
        )}
    </Box>
);

export const MapMarkerDetailModal: React.FC<MapMarkerDetailModalProps> = ({
    isOpen,
    onClose,
    type,
    tph,
    foremanGroupName,
}) => {
    if (!tph || !type) return null;

    // Helper to get block name from tph_code (first 5 characters, e.g. G1405022 -> G1405)
    const getBlockFromTphCode = (code: string) => {
        if (code && code.length >= 5) {
            return code.slice(0, 5);
        }
        return code || '-';
    };

    // Format image URL
    const getImageUrl = (imagePath?: string) => {
        if (!imagePath) return null;
        if (imagePath.startsWith('http')) return imagePath;
        try {
            const urlObj = new URL(API_BASE_URL);
            return `${urlObj.origin}${imagePath}`;
        } catch {
            return `https://sakti.gbs.id/${imagePath}`;
        }
    };

    const bccImageUrl = type === 'harvest' ? getImageUrl(tph.bcc_image) : null;
    const blockCode = getBlockFromTphCode(tph.tph_code);
    const isTph = type === 'tph';

    // Calculate distance if coordinates are present
    const distance = (!isTph && tph.latitude && tph.tph_latitude)
        ? getDistance(tph.latitude, tph.longitude || 0, tph.tph_latitude, tph.tph_longitude || 0)
        : null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={isTph ? "md" : "lg"} isCentered>
            <ModalOverlay bg="blackAlpha.300" />
            <ModalContent borderRadius="xl" overflow="hidden">
                <ModalHeader bg="customGold.500" borderBottomWidth="1px" borderColor="gray.100">
                    <Heading color="white" size="md">
                        {isTph ? 'Titik TPH' : 'Detail Input Panen'}
                    </Heading>
                </ModalHeader>
                <ModalCloseButton color="white" />
                <ModalBody py={5}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                        {/* Common Fields */}
                        {!isTph && <DetailItem label="Nomor BCC" value={tph.bcc_number} />}
                        {!isTph && <DetailItem label="Nomor SPB" value={tph.no_spb} />}
                        {!isTph && foremanGroupName && <DetailItem label="Kemandoran" value={foremanGroupName} />}
                        {!isTph && <DetailItem label="Mandor Panen" value={tph.foreman_name} />}
                        {!isTph && <DetailItem label="Kerani Panen" value={tph.created_by} />}
                        {!isTph && (
                            <DetailItem 
                                label="Pemanen" 
                                value={`${tph.harvester_name} (${tph.harvester_nip || '-'})`} 
                            />
                        )}
                        <DetailItem label="Blok" value={tph.block_code} />
                        <DetailItem label="Kode TPH" value={tph.tph_code} />
                        {/* Harvest Specific Qty Fields */}
                        {!isTph && (
                            <>
                                <DetailItem label="Jumlah Janjang (Total)" value={tph.bunch_qty} />
                                <DetailItem label="Janjang Matang" value={tph.ripe_qty} />
                                <DetailItem label="Janjang Mentah" value={tph.unripe_qty} />
                                <DetailItem label="Janjang Busuk & Jangkos" value={tph.rotten_empty_bunch_qty} />
                                <DetailItem label="Kg Brondolan" value={tph.loose_fruit_qty} />
                                <DetailItem 
                                    label="Jarak ke TPH" 
                                    value={distance !== null ? `${distance.toFixed(1)} meter` : "-"} 
                                />
                            </>
                        )}
                        
                        {/* Coordinates (conditional fields) */}
                        <DetailItem 
                            label={isTph ? "Latitude TPH" : "Latitude Input"} 
                            value={isTph ? tph.tph_latitude : tph.latitude} 
                        />
                        <DetailItem 
                            label={isTph ? "Longitude TPH" : "Longitude Input"} 
                            value={isTph ? tph.tph_longitude : tph.longitude} 
                        />
                    </Grid>

                    {/* Image Attachments (Harvest only) */}
                    {!isTph && bccImageUrl && (
                        <Box mt={6} borderTop="1px solid" borderColor="gray.100" pt={4}>
                            <Text fontSize="sm" fontWeight="bold" color="gray.500" mb={3}>
                                Foto Lampiran
                            </Text>
                            <VStack align="start" w="100%">
                                <Text fontSize="xs" color="gray.500">Foto BCC</Text>
                                <Image 
                                    src={bccImageUrl} 
                                    alt="Foto BCC" 
                                    borderRadius="lg" 
                                    objectFit="contain"
                                    maxH="240px"
                                    w="100%"
                                    fallbackSrc="https://via.placeholder.com/150?text=Foto+BCC+Tidak+Ada"
                                />
                            </VStack>
                        </Box>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};
